import { useCallback, useMemo, useRef, useState } from 'react';
import { BomLineAdd, ManualEditRequest, SmartBomComponentEdit } from '../../../types/bomVersions';
import { BomLineValues, missingFor, parsePrice, parseQuantity } from '../../../utils/bomLines';
import { BomMissingField } from '../../../types/bom.types';
import { BomSheetField, ExtractedBomRow, SheetRow } from './types';

/**
 * The extracted-BOM sheet's unsaved draft: typed cell values, appended lines
 * and lines marked for removal. Pure client state — it lives only while the
 * sheet is open (the modal unmounts it on close, which is the discard).
 *
 * Validity follows the manual-edit contract: a saved line's name may not be
 * emptied and its quantity must stay a whole number >= 1; a new line needs
 * both. Untouched gaps in extracted lines are NOT errors — they are what the
 * completeness line reports — so they never block a save. A new line left
 * entirely blank is ignored (no errors, not sent), so an accidental "Add
 * line" never blocks Save; it validates as soon as anything is typed in it.
 */

const ALL_FIELDS: BomSheetField[] = ['name', 'partNumber', 'mpn', 'manufacturer', 'description', 'quantity', 'targetPrice'];

type OptionalTextField = 'partNumber' | 'mpn' | 'manufacturer' | 'description';
const TEXT_FIELDS: OptionalTextField[] = ['partNumber', 'mpn', 'manufacturer', 'description'];

const originalText = (row: ExtractedBomRow, field: BomSheetField): string => {
  const value = row[field];
  return value === null || value === undefined ? '' : String(value);
};

const cellKey = (rowKey: string, field: BomSheetField) => `${rowKey}\u0000${field}`;

export interface BomSheetDraft {
  rows: SheetRow[];
  value: (row: SheetRow, field: BomSheetField) => string;
  isDirty: (row: SheetRow, field: BomSheetField) => boolean;
  /** Why the cell blocks Save, or undefined when it is fine. */
  errorFor: (row: SheetRow, field: BomSheetField) => string | undefined;
  setCell: (row: SheetRow, field: BomSheetField, value: string) => void;
  addLine: () => void;
  /** Saved line: toggles the removal mark. New line: drops it. */
  toggleRemove: (row: SheetRow) => void;
  request: ManualEditRequest;
  changedCount: number;
  hasChanges: boolean;
  invalidCount: number;
  /** Current values of every line that will remain, for the live completeness count. */
  remainingLines: BomLineValues[];
  /** What a line still lacks with the draft applied ([] for a removed line). */
  gapsFor: (row: SheetRow) => BomMissingField[];
}

export const useBomSheetDraft = (components: ExtractedBomRow[]): BomSheetDraft => {
  const [cells, setCells] = useState<Record<string, string>>({});
  const [addedKeys, setAddedKeys] = useState<string[]>([]);
  const [removed, setRemoved] = useState<Set<string>>(() => new Set());
  const nextAdd = useRef(1);

  const rows = useMemo<SheetRow[]>(
    () => [
      ...components.map((original, index) => ({
        key: original.componentId || original._id || `line-${index}`,
        kind: 'saved' as const,
        original,
        editable: Boolean(original.componentId),
        removed: Boolean(original.componentId && removed.has(original.componentId)),
      })),
      ...addedKeys.map((key) => ({ key, kind: 'added' as const, original: {}, editable: true, removed: false })),
    ],
    [components, addedKeys, removed]
  );

  const value = useCallback(
    (row: SheetRow, field: BomSheetField) => cells[cellKey(row.key, field)] ?? originalText(row.original, field),
    [cells]
  );

  const isBlankAdd = useCallback(
    (row: SheetRow) => row.kind === 'added' && ALL_FIELDS.every((f) => value(row, f).trim() === ''),
    [value]
  );

  const isDirty = useCallback(
    (row: SheetRow, field: BomSheetField) =>
      row.kind === 'added'
        ? value(row, field).trim() !== ''
        : value(row, field).trim() !== originalText(row.original, field).trim(),
    [value]
  );

  const errorFor = useCallback(
    (row: SheetRow, field: BomSheetField): string | undefined => {
      if (row.removed || !row.editable || isBlankAdd(row)) return undefined;
      const checkRequired = row.kind === 'added' || isDirty(row, field);
      if (!checkRequired) return undefined;
      const raw = value(row, field);
      if (field === 'name' && raw.trim() === '') return 'Give this line a name';
      if (field === 'quantity' && parseQuantity(raw) === null) return 'Quantity must be a whole number of at least 1';
      if (field === 'targetPrice' && raw.trim() !== '' && parsePrice(raw) === null) {
        return 'Target price must be a number of 0 or more';
      }
      return undefined;
    },
    [isDirty, isBlankAdd, value]
  );

  const lineValues = useCallback(
    (row: SheetRow): BomLineValues => ({
      componentId: row.original.componentId ?? null,
      name: value(row, 'name'),
      partNumber: value(row, 'partNumber'),
      mpn: value(row, 'mpn'),
      description: value(row, 'description'),
      specifications: row.original.specifications,
      quantity: parseQuantity(value(row, 'quantity')),
    }),
    [value]
  );

  const gapsFor = useCallback(
    (row: SheetRow) => (row.removed ? [] : missingFor(lineValues(row))),
    [lineValues]
  );

  const setCell = useCallback((row: SheetRow, field: BomSheetField, next: string) => {
    setCells((prev) => ({ ...prev, [cellKey(row.key, field)]: next }));
  }, []);

  const addLine = useCallback(() => {
    const key = `new-${nextAdd.current++}`;
    setAddedKeys((prev) => [...prev, key]);
  }, []);

  const toggleRemove = useCallback((row: SheetRow) => {
    if (row.kind === 'added') {
      setAddedKeys((prev) => prev.filter((k) => k !== row.key));
      return;
    }
    const id = row.original.componentId;
    if (!id) return;
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const derived = useMemo(() => {
    const edits: SmartBomComponentEdit[] = [];
    const adds: BomLineAdd[] = [];
    const removes: string[] = [];
    let invalidCount = 0;
    const remainingLines: BomLineValues[] = [];

    rows.forEach((row) => {
      if (row.removed) {
        removes.push(row.original.componentId as string);
        return;
      }
      if (isBlankAdd(row)) return;
      invalidCount += ALL_FIELDS.filter((f) => errorFor(row, f)).length;
      const text = (f: BomSheetField) => value(row, f).trim();
      remainingLines.push(lineValues(row));

      if (row.kind === 'added') {
        const add: BomLineAdd = { name: text('name'), quantity: parseQuantity(value(row, 'quantity')) ?? 0 };
        TEXT_FIELDS.forEach((f) => {
          if (text(f)) add[f] = text(f);
        });
        const price = parsePrice(value(row, 'targetPrice'));
        if (price !== null) add.targetPrice = price;
        adds.push(add);
        return;
      }

      if (!row.editable) return;
      const edit: SmartBomComponentEdit = { componentId: row.original.componentId as string };
      if (isDirty(row, 'name')) edit.name = text('name');
      // An emptied optional field is sent as null — the contract's "clear it".
      TEXT_FIELDS.forEach((f) => {
        if (isDirty(row, f)) edit[f] = text(f) || null;
      });
      if (isDirty(row, 'quantity')) edit.quantity = parseQuantity(value(row, 'quantity')) ?? undefined;
      if (isDirty(row, 'targetPrice')) edit.targetPrice = parsePrice(value(row, 'targetPrice'));
      if (Object.keys(edit).length > 1) edits.push(edit);
    });

    const request: ManualEditRequest = { source: 'buyer' };
    if (edits.length) request.components = edits;
    if (adds.length) request.adds = adds;
    if (removes.length) request.removes = removes;

    return {
      request,
      changedCount: edits.length,
      hasChanges: edits.length + adds.length + removes.length > 0,
      invalidCount,
      remainingLines,
    };
  }, [rows, value, isDirty, isBlankAdd, errorFor, lineValues]);

  return { rows, value, isDirty, errorFor, setCell, addLine, toggleRemove, gapsFor, ...derived };
};

export default useBomSheetDraft;
